import React from "react";
import { Card, Space, Popconfirm, Tag } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { HighPremiumOrder, ProductCompanyListVO, getSelectMetadataByCode } from "@ifa/utils";

interface HighPremiumOrderCardProps {
  order: HighPremiumOrder & { key: React.Key; readOnly?: boolean };
  companyList: ProductCompanyListVO[];
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
  readOnly?: boolean;
}

// 递归查找状态名称的辅助函数
const findStatusName = (items: any[], statusKey: string): string | null => {
  for (const item of items) {
    if (item.key === statusKey) {
      return item.name;
    }
    if (item.children && item.children.length > 0) {
      const found = findStatusName(item.children, statusKey);
      if (found) return found;
    }
  }
  return null;
};

export const HighPremiumOrderCard: React.FC<HighPremiumOrderCardProps> = ({ order, companyList, onEdit, onDelete, disabled = false, readOnly = false }) => {
  const companyName = companyList.find((c: any) => c.code === order.companyCode)?.companyName || companyList.find((c: any) => c.code === order.companyCode)?.name || order.companyCode || "-";

  // 获取状态显示名称
  const getStatusDisplay = () => {
    if (!order.isInternal || !order.status) {
      return order.status || "-";
    }

    const orderStatusMetadata = getSelectMetadataByCode("ORDER_STATUS");
    if (orderStatusMetadata?.items) {
      const statusName = findStatusName(orderStatusMetadata.items, order.status);
      return statusName || order.status;
    }

    return order.status;
  };

  return (
    <Card
      style={{
        marginBottom: 16,
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
      bodyStyle={{ padding: 16 }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#333",
              marginBottom: 4,
            }}
          >
            {order.orderNo || "-"}
          </div>
          <div style={{ fontSize: 14, color: "#666" }}>{companyName}</div>
        </div>

        {order.isInternal ? (
          <div></div>
        ) : (
          !readOnly && (
            <Space size="middle">
              <EditOutlined
                style={{
                  fontSize: 18,
                  color: disabled ? "#d9d9d9" : "var(--theme-primary)",
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
                onClick={disabled ? undefined : onEdit}
              />
              <Popconfirm
                title="確定刪除嗎?"
                onConfirm={(e) => {
                  e?.stopPropagation();
                  onDelete();
                }}
                okText="確定"
                cancelText="取消"
                disabled={disabled}
              >
                <DeleteOutlined
                  style={{
                    fontSize: 18,
                    color: disabled ? "#d9d9d9" : "#ff4d4f",
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            </Space>
          )
        )}
      </div>

      {/* Details Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>狀態</div>
          <div style={{ fontSize: 14, color: "#333" }}>{getStatusDisplay()}</div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>貨幣</div>
          <div style={{ fontSize: 14, color: "#333", fontWeight: 500 }}>{order.currency || "-"}</div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>年繳保費</div>
          <div
            style={{
              fontSize: 14,
              color: "#333",
              fontFamily: "monospace",
            }}
          >
            {order.annualPremium ? Number(order.annualPremium).toLocaleString() : "-"}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>一次性保費</div>
          <div
            style={{
              fontSize: 14,
              color: "#333",
              fontFamily: "monospace",
            }}
          >
            {order.oneTimePremium ? Number(order.oneTimePremium).toLocaleString() : "-"}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>保單號碼</div>
          <div style={{ fontSize: 14, color: "#333" }}>{order.policyNo || "-"}</div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>生效日期</div>
          <div style={{ fontSize: 14, color: "#666" }}>{order.applicationTime || "-"}</div>
        </div>
      </div>
    </Card>
  );
};
